import {
  Typography,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import { useEffect, useState, MouseEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AppHeader } from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-header';
import {
  DescriptionCollapse,
  DatesCollapse,
  CommentsCollapse,
  DetailsCollapse,
  IssueLinksCollapse,
  PeopleCollapse,
  AttachmentsCollapse,
} from '@eng-mono/atlassian/jira/mojira-bugtracker/frontend-feature-issue-list';
import {
  JsmIssueBase,
  projectPortalMap,
  postJQLSearchResponse,
  getKeyByProject,
} from '@eng-mono/atlassian/jira/mojira-bugtracker/util-issue-list-fe';
import { MojiraBugtrackerBackendPaths } from '@eng-mono/atlassian/jira/mojira-bugtracker/util-resolver';
import { useBackendAdapter } from '@eng-mono/atlassian/shared/frontend-ui-backend-adapter';
import { useDocumentTitle } from '@eng-mono/atlassian/shared/frontend-ui-contexts';

import styles from './issue-single-view.module.scss';

interface IssueSingleViewProps {
  siteUrl: string;
}

export function IssueSingleView({ siteUrl }: IssueSingleViewProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const navigate = useNavigate();
  const { issueKey, project } = useParams();
  useDocumentTitle(`Mojira - Issue ${issueKey}`);

  const portal = getKeyByProject(project);
  const { invoke } = useBackendAdapter();

  const [issue, setIssue] = useState<JsmIssueBase | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    details: true,
    description: true,
    attachments: true,
    issueLinks: true,
    activity: true,
    people: true,
    dates: true,
  });

  const toggleSection = (section: string) => {
    setExpandedSections((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }));
  };

  const { portalIconUrl: headerIconUrl, portalName } =
    projectPortalMap[portal as unknown as keyof typeof projectPortalMap];

  const navigateToIssue = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (issue) {
      navigate(`/browse/${project}/issues/${issue.key}`, { state: issue });
    }
  };

  useEffect(() => {
    const fetchIssue = async () => {
      try {
        setLoading(true);
        setError(null);

        const search = `key = ${issueKey}`;
        const response = await invoke<postJQLSearchResponse>(
          MojiraBugtrackerBackendPaths.jqlSearchPost,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            advanced: true,
            search,
            project: project || '',
            isForge: false,
            filter: 'all',
            page: 0,
            maxResults: 1,
          }
        );

        if (!response) {
          setError('Failed to fetch issue data');
          return;
        }

        if (!response.issues || response.issues.length === 0) {
          setError('Issue not found');
          return;
        }

        setIssue(response.issues[0]);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'An error occurred while fetching the issue'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchIssue();
  }, [invoke, issueKey, project]);

  return (
    <div className={styles['app-container']}>
      <AppHeader
        loginDestination={`${siteUrl}/servicedesk/customer/portal/${portal}/${issueKey}`}
      />

      {error && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '90vh',
            padding: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Typography variant="h5" align="center" color="error">
            {error}
          </Typography>
        </Box>
      )}

      {!issue && !error && loading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: 'auto',
          }}
        >
          <CircularProgress size="4rem" />
        </Box>
      )}

      {issue && !loading && !error && (
        <main>
          <Container maxWidth="xl" className={styles['main-container']}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: { xs: 2, sm: 3, md: 4 },
                width: '100%',
                boxSizing: 'border-box',
                marginBottom: { xs: 2, sm: 3 },
                borderBottom: '1px solid #ccc',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1, sm: 2 },
                  marginBottom: { xs: 1, sm: 2 },
                }}
              >
                <img
                  src={headerIconUrl}
                  alt={issue.fields.issuetype.name}
                  style={{
                    width: isMobile ? '24px' : '32px',
                    height: isMobile ? '24px' : '32px',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    wordBreak: 'break-word',
                  }}
                >
                  {portalName + ' / '}
                  <a
                    href={`/browse/${project}/issues/${issue.key}`}
                    onClick={navigateToIssue}
                    className={styles['issue-link']}
                  >
                    {issue.key}
                  </a>
                </Typography>
              </Box>
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                sx={{
                  fontWeight: 'bold',
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                }}
              >
                {issue.fields.summary}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: { xs: 2, sm: 3, md: 4 },
                padding: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Box
                sx={{
                  width: { xs: '100%', md: '70%' },
                  order: { xs: 2, md: 1 },
                }}
              >
                {/* Details collapsible section */}
                {issue.fields && (
                  <DetailsCollapse
                    issue={issue}
                    expanded={expandedSections.details}
                    toggleSection={() => toggleSection('details')}
                    portalKey={portal as string}
                  />
                )}

                {/* Description collapsible section */}
                {(() => {
                  return (
                    issue.renderedFields.description && (
                      <DescriptionCollapse
                        issue={issue}
                        expanded={expandedSections.description}
                        toggleSection={() => toggleSection('description')}
                      />
                    )
                  );
                })()}

                {/* Attachments collapsible section */}
                {issue?.fields.attachment?.length ? (
                  <AttachmentsCollapse
                    issue={issue}
                    expanded={expandedSections.attachments}
                    toggleSection={() => toggleSection('attachments')}
                  />
                ) : null}

                {/* Issue links collapsible section */}
                {issue.fields.issuelinks?.length ? (
                  <IssueLinksCollapse
                    issue={issue}
                    expanded={expandedSections.issueLinks}
                    toggleSection={() => toggleSection('issueLinks')}
                  />
                ) : null}

                {/* Comments collapsible section */}
                <CommentsCollapse
                  issue={issue}
                  expanded={expandedSections.activity}
                  toggleSection={() => toggleSection('activity')}
                />
              </Box>

              {/* Second column for People and Dates */}
              <Box
                sx={{
                  width: { xs: '100%', md: '30%' },
                  order: { xs: 1, md: 2 },
                }}
              >
                <PeopleCollapse
                  issue={issue}
                  expanded={expandedSections.people}
                  toggleSection={() => toggleSection('people')}
                />

                <DatesCollapse
                  issue={issue}
                  expanded={expandedSections.dates}
                  toggleSection={() => toggleSection('dates')}
                />
              </Box>
            </Box>
          </Container>
        </main>
      )}
    </div>
  );
}

export default IssueSingleView;
